import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseISO, isValid } from 'date-fns';
import { formatearFecha } from '../../utils/fecha';
import BannerFichas from '../../components/BannerFichas/BannerFichas';
import Navbar from '../../components/Navbar/Navbar';
import SessionBar from '../../components/SessionBar/SessionBar';
import Footer from '../../components/Footer/Footer';
import { fetchUserData } from '../../utils/api';
import axiosInstance from '../../services/AxiosInstance';
import { ROUTES } from '../../const/routes';
import BotonChico04PdfPago from '../../components/Botones/BotonChico04PdfPago/BotonChico04PdfPago';
import BotonChico03 from '../../components/Botones/BotonChico03/BotonChico03';
import { useTranslation } from 'react-i18next';

const MOVIMIENTOS_POR_PAGINA = 10;

const Movimientos = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);

  const [movimientos, setMovimientos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [filtro, setFiltro] = useState('All');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [errorFecha, setErrorFecha] = useState('');
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    fetchUserData(navigate, setUserData);
  }, [navigate]);

  const fetchMovimientos = useCallback(async () => {
    setCargando(true);
    try {
      const params = {
        page: currentPage,
        limit: MOVIMIENTOS_POR_PAGINA,
        filtro,
      };

      if (fechaInicio) params.fechaInicio = fechaInicio;
      if (fechaFin) params.fechaFin = fechaFin;

      const response = await axiosInstance.get(
        '/api/movimientoSaldo/paginados',
        { params }
      );

      const { data, totalPages } = response.data;

      const movimientosFormateados = data.map((movimiento) => ({
        ...movimiento,
        hora_movimiento_local: formatearFecha(movimiento.hora_movimiento),
      }));

      setMovimientos(movimientosFormateados);
      setTotalPaginas(totalPages || 1);
    } catch (error) {
      console.error('Error al obtener los movimientos:', error);
      setMovimientos([]);
      setTotalPaginas(1);
    } finally {
      setCargando(false);
    }
  }, [currentPage, filtro, fechaInicio, fechaFin]);

  useEffect(() => {
    if (!errorFecha) {
      fetchMovimientos();
    }
  }, [fetchMovimientos, errorFecha]);

  const getAccentColor = useCallback((tipo) => {
    switch (tipo) {
      case 'Prize':
        return {
          border: 'border-l-emerald-500',
          badge: 'bg-emerald-500/20 text-emerald-400',
        };
      case 'Bet':
        return {
          border: 'border-l-pink-500',
          badge: 'bg-pink-500/20 text-pink-400',
        };
      case 'Balance Load':
        return {
          border: 'border-l-sky-500',
          badge: 'bg-sky-500/20 text-sky-400',
        };
      default:
        return {
          border: 'border-l-gray-500',
          badge: 'bg-gray-500/20 text-gray-400',
        };
    }
  }, []);

  const handleFechaInicioChange = (e) => {
    const fecha = e.target.value;
    if (!fecha) {
      setFechaInicio('');
      setErrorFecha('');
      setCurrentPage(1);
      return;
    }
    const fechaISO = parseISO(fecha);
    if (!isValid(fechaISO)) {
      setErrorFecha(t('movements.invalidStartDate'));
      return;
    }
    setErrorFecha('');
    setFechaInicio(fecha);
    setCurrentPage(1);
  };

  const handleFechaFinChange = (e) => {
    const fecha = e.target.value;
    if (!fecha) {
      setFechaFin('');
      setErrorFecha('');
      setCurrentPage(1);
      return;
    }
    const fechaISO = parseISO(fecha);
    if (!isValid(fechaISO)) {
      setErrorFecha(t('movements.invalidEndDate'));
      return;
    }
    setErrorFecha('');
    setFechaFin(fecha);
    setCurrentPage(1);
  };

  const handleResetFechas = () => {
    setFechaInicio('');
    setFechaFin('');
    setErrorFecha('');
    setCurrentPage(1);
  };

  return (
    <div>
      <BannerFichas />
      <div className="bg-gradient-to-b from-slate-950 to-violet-950 min-h-screen">
        <Navbar />
        {userData && <SessionBar />}
        <div className="flex flex-col items-center py-6 md:py-10 px-2 lg:px-10">
          <h2 className="text-lg md:text-xl lg:text-2xl text-yellow-500 font-bold mb-3 md:mb-4">
            {t('movements.title')}
          </h2>
          <p className="text-[10px] md:text-xs lg:text-sm text-gray-300 mb-4 md:mb-8 text-center">
            {t('movements.subtitle')}
          </p>
          {/* Filtro */}
          <div className="mb-4 md:mb-6 flex flex-col lg:flex-row items-center gap-2">
            <label className="text-gray-300 text-xs md:text-sm font-medium">
              {t('movements.filterLabel')}
            </label>
            <select
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value);
                setCurrentPage(1);
              }}
              className="appearance-none bg-gray-700/80 text-white py-2 px-4 pr-10 rounded-lg text-xs md:text-sm border border-gray-600 focus:border-white focus:outline-none transition-colors cursor-pointer bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22white%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%2001.02-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.5rem_center] bg-no-repeat"
            >
              <option value="All">{t('movements.filterAll')}</option>
              <option value="Prize">{t('movements.filterPrize')}</option>
              <option value="Bet">{t('movements.filterBet')}</option>
              <option value="Balance Load">{t('movements.filterDeposit')}</option>
            </select>
          </div>
          {/* Filtro por fechas */}
          <div className="mb-4 md:mb-6 flex flex-col lg:flex-row gap-2 lg:space-x-4 justify-center items-center">
            <div className="flex items-center gap-2">
              <label className="text-gray-300 text-xs md:text-sm font-medium">
                {t('movements.fromLabel')}
              </label>
              <input
                type="date"
                value={fechaInicio}
                onChange={handleFechaInicioChange}
                className="bg-gray-700/80 text-white py-2 px-4 rounded-lg text-xs md:text-sm border border-gray-600 focus:border-white focus:outline-none transition-colors cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-gray-300 text-xs md:text-sm font-medium">
                {t('movements.untilLabel')}
              </label>
              <input
                type="date"
                value={fechaFin}
                onChange={handleFechaFinChange}
                className="bg-gray-700/80 text-white py-2 px-4 rounded-lg text-xs md:text-sm border border-gray-600 focus:border-white focus:outline-none transition-colors cursor-pointer"
              />
            </div>
            <BotonChico03
              texto={t('movements.resetDatesButton')}
              bgColor={'red'}
              funcion={handleResetFechas}
            />
          </div>
          {errorFecha && (
            <div className="mb-4 text-red-500 text-sm">{errorFecha}</div>
          )}
          {/* Contenido */}
          <div className="w-full">
            <div
              className={`bg-bgContenedorOscuro rounded-lg px-2 md:px-5
                      border-solid border-4 border-borderContenedorOscuro shadow-shadowContenedorOscuro
                      shadow-[0_0_20px_rgba(100,116,139,0.9)] transition-all duration-300 ease-in-out hover:shadow-[0_0_30px_rgba(100,116,139,1)]`}
            >
              {/* Vista Desktop - Tabla */}
              <div className="hidden md:block">
                <div className="w-full grid grid-cols-7 text-yellow-500 py-4 px-4 text-xs font-semibold uppercase tracking-wider items-center border-b border-gray-700/50">
                  <div className="text-center">{t('movements.columnType')}</div>
                  <div className="text-center">{t('movements.columnAmount')}</div>
                  <div className="text-center">{t('movements.columnDateTime')}</div>
                  <div className="text-center">{t('movements.columnDescription')}</div>
                  <div className="text-center">{t('movements.columnPreviousBalance')}</div>
                  <div className="text-center">{t('movements.columnUpdatedBalance')}</div>
                  <div className="text-center">{t('movements.columnDescription')}</div>
                </div>
                <div className="w-full">
                  {cargando ? (
                    <div className="text-center text-gray-300 py-10 animate-pulse">
                      {t('movements.loading')}
                    </div>
                  ) : movimientos.length === 0 ? (
                    <div className="text-center text-gray-300 py-10">
                      {t('movements.noRecords')}
                    </div>
                  ) : (
                    movimientos.map((movimiento, index) => {
                      const accent = getAccentColor(movimiento.tipo_movimiento);
                      return (
                        <div
                          key={movimiento.id}
                          className={`grid grid-cols-7 py-3 px-4 text-center text-gray-200 text-xs border-l-4 ${accent.border} border-b border-gray-700/30 transition-colors duration-150 hover:bg-white/5 ${
                            index % 2 === 0
                              ? 'bg-white/[0.03]'
                              : 'bg-transparent'
                          }`}
                        >
                          <div className="py-1 flex justify-center items-center">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${accent.badge}`}
                            >
                              {t(`backend.movementType.${movimiento.tipo_movimiento}`, movimiento.tipo_movimiento)}
                            </span>
                          </div>
                          <div className="py-1 flex justify-center items-center">
                            ${movimiento.saldo_movimiento}
                          </div>
                          <div className="py-1 flex justify-center items-center text-gray-400">
                            {movimiento.hora_movimiento_local}
                          </div>
                          <div className="py-1 flex justify-center items-center text-gray-400">
                            {movimiento.descripcion}
                          </div>
                          <div className="py-1 flex justify-center items-center">
                            ${movimiento.saldo_previo}
                          </div>
                          <div className="py-1 flex justify-center items-center">
                            ${movimiento.saldo_actualizado}
                          </div>
                          <div className="py-1 flex justify-center items-center">
                            {movimiento.tipo_movimiento === 'Balance Load' ? (
                              <BotonChico04PdfPago
                                texto={t('movements.downloadButton')}
                                bgColor="green"
                                idMovimientoSaldo={movimiento.id}
                              />
                            ) : movimiento.id_jugada ? (
                              <BotonChico03
                                texto={t('historyDetail.detailsButton')}
                                bgColor="blue"
                                funcion={() =>
                                  navigate(
                                    `${ROUTES.detallesJugada}/${movimiento.id_jugada}`,
                                    { state: { from: 'movimientos' } }
                                  )
                                }
                              />
                            ) : null}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Vista Móvil - Tarjetas */}
              <div className="md:hidden py-4 space-y-3">
                {cargando ? (
                  <div className="text-center text-gray-300 py-10 animate-pulse">
                    {t('movements.loading')}
                  </div>
                ) : movimientos.length === 0 ? (
                  <div className="text-center text-gray-300 py-10">
                    {t('movements.noRecords')}
                  </div>
                ) : (
                  movimientos.map((movimiento) => {
                    const accent = getAccentColor(movimiento.tipo_movimiento);
                    return (
                      <div
                        key={movimiento.id}
                        className={`bg-gray-800/60 text-gray-200 rounded-lg p-3 text-[10px] sm:text-xs border-l-4 ${accent.border} border border-gray-700/30`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${accent.badge}`}
                          >
                            {movimiento.tipo_movimiento}
                          </span>
                          <span className="text-gray-500 text-[9px] sm:text-[10px]">
                            {movimiento.hora_movimiento_local}
                          </span>
                        </div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-400">{t('movements.mobileAmount')}</span>
                          <span className="font-medium">
                            ${movimiento.saldo_movimiento}
                          </span>
                        </div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-400">{t('movements.mobileDescription')}</span>
                          <span className="text-right ml-2 text-[9px] sm:text-[10px]">
                            {movimiento.descripcion}
                          </span>
                        </div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-400">{t('movements.mobilePreviousBalance')}</span>
                          <span className="font-medium">
                            ${movimiento.saldo_previo}
                          </span>
                        </div>
                        <div className="flex justify-between mb-3">
                          <span className="text-gray-400">
                            {t('movements.mobileUpdatedBalance')}
                          </span>
                          <span className="font-medium">
                            ${movimiento.saldo_actualizado}
                          </span>
                        </div>
                        <div className="flex justify-center">
                          {movimiento.tipo_movimiento === 'Balance Load' ? (
                            <BotonChico04PdfPago
                              texto={t('movements.downloadButton')}
                              bgColor="green"
                              idMovimientoSaldo={movimiento.id}
                            />
                          ) : movimiento.id_jugada ? (
                            <BotonChico03
                              texto={t('historyDetail.detailsButton')}
                              bgColor="blue"
                              funcion={() =>
                                navigate(
                                  `${ROUTES.detallesJugada}/${movimiento.id_jugada}`,
                                  { state: { from: 'movimientos' } }
                                )
                              }
                            />
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Controles de paginación */}
              <div className="flex flex-wrap justify-center items-center my-6 md:my-10 gap-2 md:gap-4">
                <BotonChico03
                  texto={t('movements.previousButton')}
                  bgColor={currentPage === 1 ? 'gray' : 'pink'}
                  funcion={() => {
                    if (currentPage > 1) {
                      setCurrentPage((prev) => prev - 1);
                    }
                  }}
                />
                <span className="text-gray-300 py-1 px-3 whitespace-nowrap text-xs md:text-base">
                  {currentPage} / {totalPaginas}
                </span>
                <BotonChico03
                  texto={t('movements.nextButton')}
                  bgColor={currentPage === totalPaginas ? 'gray' : 'blue'}
                  funcion={() => {
                    if (currentPage < totalPaginas) {
                      setCurrentPage((prev) => prev + 1);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Movimientos;
